'use client';

import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const WedgeOfferSection = () => {
  return (
    <section className="py-24 lg:py-32 bg-gray-50 relative overflow-hidden">
      <div className="absolute inset-0 bg-[linear-gradient(rgba(10,37,64,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(10,37,64,0.02)_1px,transparent_1px)] bg-[size:64px_64px]"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-300 rounded-none">
              <span className="text-sm font-bold font-cta text-[#0A2540] tracking-wider uppercase">Assessment • Benchmarking • Recognition</span>
            </div>
          </div>

          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-headline text-[#0A2540] mb-6 leading-tight">
              AI Leadership Capability Snapshot
            </h2>
            <p className="text-xl md:text-2xl text-gray-700 font-body leading-relaxed max-w-3xl mx-auto">
              A 30-day diagnostic assessment evaluating organisational maturity, leadership readiness, and capacity to lead AI adoption at scale—the route to AI Change Leader recognition.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 mb-16">
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold font-headline text-[#0A2540] mb-4">What It Delivers</h3>
                <ul className="space-y-4">
                  {[
                    'Independent assessment against the AI Change Leader Standard',
                    'Maturity benchmarking across governance, capability, and adoption',
                    'Evidence-based evaluation: recognised, development pathway, or not yet ready',
                    'Clear roadmap for organisations seeking to meet the standard',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-6 h-6 bg-[#0A2540] flex items-center justify-center mt-1">
                        <Icon name="CheckIcon" size={16} variant="solid" className="text-white" />
                      </div>
                      <span className="text-lg text-gray-700 font-body">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-6 bg-white border-2 border-gray-200">
                <h4 className="text-lg font-bold font-headline text-[#0A2540] mb-2">Assessment, Not Consulting</h4>
                <p className="text-base text-gray-700 font-body leading-relaxed">
                  This is an independent diagnostic assessment, not a consulting engagement. We evaluate your organisation's maturity against defined standards. Recognition is earned through evidence of capability, governance, and readiness—not through purchasing services.
                </p>
              </div>
            </div>

            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold font-headline text-[#0A2540] mb-4">Who It's For</h3>
                <div className="space-y-4">
                  {[
                    { icon: 'UserGroupIcon', title: 'Senior Leaders', desc: 'Accountable for AI investment decisions' },
                    { icon: 'CogIcon', title: 'Transformation Executives', desc: 'Leading organisation-wide change' },
                    { icon: 'BuildingOfficeIcon', title: 'Boards & Governance', desc: 'Seeking assurance and confidence to scale' },
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-4 p-4 bg-white border-2 border-gray-200 hover:border-[#0A2540] transition-colors">
                      <div className="flex-shrink-0 w-12 h-12 bg-[#0A2540] flex items-center justify-center">
                        <Icon name={item.icon as any} size={24} variant="solid" className="text-white" />
                      </div>
                      <div>
                        <h4 className="text-lg font-bold font-headline text-[#0A2540] mb-1">{item.title}</h4>
                        <p className="text-base text-gray-600 font-body">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-6 bg-[#0A2540] text-white">
                <h4 className="text-lg font-bold font-headline mb-2">The AI Change Leader Designation</h4>
                <p className="text-base font-body leading-relaxed text-white/90">
                  Organisations that meet the standard earn recognition as AI Change Leaders—a designation that signals credibility, maturity, and responsible stewardship. This mark demonstrates to stakeholders, partners, and regulators that your organisation leads AI adoption with confidence and accountability.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center">
            <div className="inline-block">
              <div className="flex flex-col sm:flex-row items-center gap-4 mb-6">
                <Link
                  href="/discovery-call-booking"
                  className="inline-flex items-center justify-center gap-3 px-10 py-5 bg-[#0A2540] text-white font-semibold font-cta text-lg rounded-none hover:bg-[#0D2F52] transition-all duration-300 w-full sm:w-auto"
                >
                  <span>Start Diagnostic</span>
                  <Icon name="ArrowRightIcon" size={20} variant="solid" />
                </Link>
                <Link
                  href="/contact"
                  className="inline-flex items-center gap-2 px-10 py-5 border-2 border-[#0A2540] text-[#0A2540] font-semibold font-cta text-lg rounded-none hover:bg-[#0A2540] hover:text-white transition-all duration-300 w-full sm:w-auto justify-center"
                >
                  Learn About Recognition
                  <Icon name="CalendarIcon" size={20} variant="outline" />
                </Link>
              </div>
              <p className="text-sm text-gray-600 font-body">
                Independent assessment • Evidence-based recognition • Ongoing stewardship
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WedgeOfferSection;